import { ReactNode } from "react";
import { cn } from "@shared/utils/cn";

interface BookingChromeProps {
  children: ReactNode;
  /**
   * Card width cap.
   * - 'wide' (default) — `max-w-2xl` (672px). The booking flow and most
   *   standalone pages (Profile, Review, confirmations, gift cards, etc.)
   * - 'narrow' — `max-w-md` (448px). Used by tighter content pages like
   *   ExperiencesPage where the wider card feels too loose.
   */
  width?: "narrow" | "wide";
  /**
   * Apply `flex flex-col` to the inner card so children flow as columns.
   * Pages with stacked sections (header + content + footer) pass `true`.
   */
  flexCol?: boolean;
  /** Additional classes appended to the inner card. */
  cardClassName?: string;
}

/**
 * Standard mobile-first chrome for the booking widget.
 *
 * - Below `lg` (1024px): full-bleed — no shadow, no top corners, no outer
 *   padding. Bottom corners stay rounded for a soft visual close.
 * - At `lg+`: floating card with shadow, all corners rounded, outer padding.
 *
 * Auth pages (AuthLoginPage, AuthSignupPage, ForgotPasswordPage,
 * ResetPasswordPage) have a different chrome — they vertically center the
 * card and render a "Powered by Meitre" sibling outside it — and don't use
 * this component.
 */
export function BookingChrome({
  children,
  width = "wide",
  flexCol = false,
  cardClassName,
}: BookingChromeProps) {
  return (
    <div className="min-h-screen flex flex-col items-center p-0 lg:p-4">
      <div
        className={cn(
          // Single source of restaurant theme background. Children must NOT
          // add their own bg-card or they'll stack opacities on translucent
          // restaurants (cosme, mishiguene, etc.). The sticky header uses
          // backdrop-blur instead so it stays visible when detached from chrome.
          // No `relative z-10` and no `animate-fade-in`: position/opacity
          // animation would create a stacking context that confines the
          // sticky header's backdrop-filter — for the duration of the
          // animation, the header samples the bg-card instead of the body
          // image, causing a visible white→transparent flicker on each step.
          "w-full bg-card",
          "shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl",
          "overflow-visible lg:overflow-hidden",
          width === "narrow" ? "max-w-md" : "max-w-2xl",
          flexCol && "flex flex-col",
          cardClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
